// ConfirmationScreen.js

import React, { useState, useRef, useEffect, useContext, useMemo } from "react";
import {
  PButtonPure,
  PDivider,
  PSpinner,
  PInlineNotification,
} from "@porsche-design-system/components-react";
import { useTranslation } from "react-i18next";
import useTexts from "MainApp/assets/utils/hooks/useTexts";
import { DateTime } from "luxon";
import { loadStripe } from "@stripe/stripe-js";
import "./ConfirmationScreen.css";
import BlurFade from "@/components/magicui/blur-fade";
import {
  dropShadowLowStyle,
  spacingFluidLarge,
  spacingFluidMedium,
  spacingFluidSmall,
  spacingFluidXLarge,
  spacingFluidXSmall,
  textXLargeStyle,
} from "@porsche-design-system/components-js/styles";
import RenderHumanDataFields from "./RenderHumanDataFields/RenderHumanDataFields";
import PersonalInfoFields from "./PersonalInfoFields/PersonalInfoFields";
import StickyDrawer from "Components/StickyDrawer/StickyDrawer";
import { useGetTileBaseStyleDrop } from "MainApp/assets/visual/styles/globalStyles/globalJsxStyles";
import RenderStaticData from "./RenderStaticData/RenderStaticData";

import axios from "axios"; // anadimos axios para crear clientes alternativos
import axiosInstance2 from "axiosConfig";
import { AuthContext } from "MainApp/assets/utils/context/AuthContext";
import { ToursContext } from "MainApp/assets/utils/context/TourContext";
import AutoTuktukModelsSection from "./TuktukModelsSection/AutoTuktukModelsSection";
import CText from "Components/CComponents/CText/CText";
import { calculateVehicleTierPricing } from "./vehicleTierPricing";
import { resolveCustomPickupConfig } from "./customPickupPricing";
import { getCheckoutValidationMessages } from "./checkoutValidationMessages";

const ConfirmationScreen = ({
  date,
  time,
  onBack,
  formData,
  setFormData,
  isPhone,
  tourDuration,
  slug,
  isDrawerOpen,
  setIsDrawerOpen,
}) => {
  const { fetchOptionalFields, optionalFields, axiosInstance, userData } =
    useContext(AuthContext);
  const { setUserTour } = useContext(ToursContext);
  const tileBasedStyle = useGetTileBaseStyleDrop();

  const textOrigin = "confirmationScreen";
  const { i18n } = useTranslation(textOrigin);
  const { texts } = useTexts(textOrigin);
  const phoneRef = useRef(null);
  const countryCodeRef = useRef(null);

  // Estados
  const [hasMinors, setHasMinors] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [minors, setMinors] = useState([]);
  const [minorsTotal, setMinorsTotal] = useState(0);
  const [adults, setAdults] = useState(1);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [validationMessages, setValidationMessages] = useState({});
  const [tuktukModels, setTuktukModels] = useState([]);

  const isMinVehicleSmall = formData.vehiclesSmall;
  const isPersonsInsteadTime = formData.personsInsteadTime;
  const usesPeopleAndTimesPricing = Boolean(
    formData.usesPeopleAndTimesPricing || formData.uses_people_and_times_pricing
  );
  const usesPeoplePriceTiersInFrontend = Boolean(
    formData.frontendUsesPeoplePriceTiers ??
      formData.frontend_uses_people_price_tiers
  );
  const locksPeopleSelection = Boolean(
    isPersonsInsteadTime &&
      usesPeopleAndTimesPricing &&
      usesPeoplePriceTiersInFrontend
  );

  const getDurationForPricing = (durations, selectedDurationMinutes) => {
    const list = Array.isArray(durations) ? durations : [];
    if (!list.length) return null;

    const selected = Number(selectedDurationMinutes || 0);
    const byDuration = list.find(
      (d) => Number(d?.duration || 0) === Number(selected || 0)
    );
    return byDuration || list[0] || null;
  };

  const getPriceByPeopleRanges = (
    ranges,
    persons,
    fallbackUnitPrice,
    fallbackDurationPrice
  ) => {
    const list = (Array.isArray(ranges) ? ranges : [])
      .map((range) => ({
        min_people: Number(range?.min_people ?? range?.minPeople ?? 1) || 1,
        max_people:
          (() => {
            const rawMax = range?.max_people ?? range?.maxPeople;
            if (rawMax === "" || rawMax == null) return null;
            const parsed = Number(rawMax);
            return Number.isFinite(parsed) ? parsed : null;
          })(),
        price: Number(range?.price ?? 0) || 0,
      }))
      .sort((a, b) => a.min_people - b.min_people);

    if (!list.length) {
      return Number(fallbackDurationPrice ?? fallbackUnitPrice ?? 0) || 0;
    }

    const pax = Number(persons || 0);
    if (!Number.isFinite(pax) || pax <= 0) {
      return Number(fallbackDurationPrice ?? fallbackUnitPrice ?? 0) || 0;
    }

    const match = list.find(
      (range) =>
        pax >= range.min_people &&
        (range.max_people == null || pax <= range.max_people)
    );

    if (match) return Number(match.price || 0) || 0;
    return Number(fallbackDurationPrice ?? fallbackUnitPrice ?? 0) || 0;
  };

  const durationForPricing = useMemo(
    () =>
      getDurationForPricing(
        formData.durations,
        formData.selectedDuration || tourDuration
      ),
    [formData.durations, formData.selectedDuration, tourDuration]
  );
  const selectedVehiclesForPricing = useMemo(
    () =>
      (formData.tuktukModels || [])
        .map((selectedModel) => {
          const model = tuktukModels.find(
            (candidate) => candidate.value === selectedModel.value
          );
          if (!model) return null;

          return {
            value: model.value,
            label: model.label,
            maxPeople: model.maxPeople,
            units: Number(selectedModel.units ?? 0) || 0,
          };
        })
        .filter(Boolean),
    [formData.tuktukModels, tuktukModels]
  );
  const pricingPreview = useMemo(
    () =>
      calculateVehicleTierPricing({
        peopleRanges:
          durationForPricing?.people_prices ?? durationForPricing?.peoplePrices,
        selectedVehicles: selectedVehiclesForPricing,
        totalPeople: formData.persons,
        fallbackUnitPrice: durationForPricing?.price ?? formData.priceUnit,
        preferLowerTier: Boolean(
          formData.preferLowerPeoplePriceTier ??
            formData.prefer_lower_people_price_tier
        ),
        locale: i18n.language,
      }),
    [
      durationForPricing,
      selectedVehiclesForPricing,
      formData.persons,
      formData.priceUnit,
      formData.preferLowerPeoplePriceTier,
      formData.prefer_lower_people_price_tier,
      i18n.language,
    ]
  );
  const customPickupConfig = useMemo(
    () =>
      resolveCustomPickupConfig({
        optionalFields,
        formData,
      }),
    [optionalFields, formData]
  );

  // Si el tour funciona por personas, fijamos personas segun el tramo elegido:
  // prioriza max_people; si el tramo es abierto (sin max), usa min_people.
  useEffect(() => {
    if (!locksPeopleSelection) return;

    const selectedRange =
      formData.selectedPeopleRange ?? formData.selected_people_range ?? null;
    const minPeople = Math.max(
      1,
      Number(selectedRange?.min_people ?? selectedRange?.minPeople ?? 1) || 1
    );
    const parsedMax = Number(
      selectedRange?.max_people ?? selectedRange?.maxPeople
    );
    const selectedByRange =
      Number.isFinite(parsedMax) && parsedMax > 0 ? parsedMax : minPeople;
    const selectedByTour =
      Number(formData.selectedPeopleCount ?? formData.selected_people_count) ||
      selectedByRange ||
      Number(tourDuration || 0) ||
      1;

    setFormData((prev) => {
      const currentPersons = Number(prev.persons) || 0;
      const currentAdults = Number(prev.adults) || 0;
      const nextPersons = Math.max(1, Number(selectedByTour) || 1);
      if (currentPersons === nextPersons && currentAdults === nextPersons) {
        return prev;
      }
      return {
        ...prev,
        persons: nextPersons,
        adults: nextPersons,
        minorsTotal: 0,
      };
    });
  }, [
    locksPeopleSelection,
    formData.selectedPeopleRange,
    formData.selected_people_range,
    formData.selectedPeopleCount,
    formData.selected_people_count,
    setFormData,
    tourDuration,
  ]);

  // Estado para errores de componentes hijos
  const [errors, setErrors] = useState({});

  // Stripe
  const [stripeKey, setStripeKey] = useState(null);

  // Mini consola
  const [miniConsoleOpen, setMiniConsoleOpen] = useState(false);
  const [miniConsoleLogs, setMiniConsoleLogs] = useState([]);

  // Helpers mini consola
  const pushLog = (entry) => {
    setMiniConsoleLogs((prev) => [
      ...prev,
      { ts: new Date().toISOString(), ...entry },
    ]);
  };
  const toSecs = (ms) => Math.floor(ms / 1000);

  // Captura errores globales y console.error
  useEffect(() => {
    const onError = (event) => {
      pushLog({
        level: "error",
        message: event?.message || "window.onerror",
        stack: event?.error?.stack || "",
        where: `${event?.filename || ""}:${event?.lineno || ""}:${
          event?.colno || ""
        }`,
      });
    };
    const onUnhandled = (event) => {
      pushLog({
        level: "error",
        message: "Unhandled Promise Rejection",
        stack:
          (event?.reason && (event.reason.stack || event.reason.message)) ||
          JSON.stringify(event?.reason || {}),
      });
    };
    const originalError = console.error;
    console.error = (...args) => {
      try {
        pushLog({
          level: "error",
          message: args
            .map((a) => (a instanceof Error ? a.message : String(a)))
            .join(" "),
          stack: args.find((a) => a instanceof Error)?.stack || "",
        });
      } catch {}
      originalError(...args);
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandled);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onUnhandled);
      console.error = originalError;
    };
  }, []);

  // stripePromise
  const stripePromise = useMemo(
    () => (stripeKey ? loadStripe(stripeKey) : null),
    [stripeKey]
  );

  // Axios alternativos (baseURL heredada de tu axiosInstance si existe)
  const baseURL =
    (axiosInstance &&
      axiosInstance.defaults &&
      axiosInstance.defaults.baseURL) ||
    "/api";

  // 1) Publico: sin cookies ni Authorization
  const axiosPublic = useMemo(() => {
    const a = axios.create({ baseURL, withCredentials: false });
    a.interceptors.request.use((cfg) => {
      if (cfg.headers) {
        delete cfg.headers.Authorization;
        if (cfg.headers.common) delete cfg.headers.common.Authorization;
      }
      return cfg;
    });
    return a;
  }, [baseURL]);

  // 2) Cookies-only: withCredentials true, pero eliminamos Authorization
  const axiosCookieOnly = useMemo(() => {
    const a = axios.create({ baseURL, withCredentials: true });
    a.interceptors.request.use((cfg) => {
      if (cfg.headers) {
        delete cfg.headers.Authorization;
        if (cfg.headers.common) delete cfg.headers.common.Authorization;
      }
      return cfg;
    });
    return a;
  }, [baseURL]);

  // 3) Auth completo: tu axiosInstance (cookies + Authorization, si la pones ahi)
  const axiosAuth = axiosInstance;

  // CSRF para checkout
  const getCsrfToken = async () => {
    try {
      const response = await axiosInstance2.get("/csrf-token");
      return response.data.csrf_token;
    } catch (error) {
      console.error("Error fetching CSRF token:", error);
      throw error;
    }
  };

  // Stripe key
  useEffect(() => {
    const fetchStripeKey = async () => {
      try {
        const response = await axiosInstance2.get(`/getStripeToken/${slug}`);
        const fetchedStripeKey = response.data;
        setStripeKey(fetchedStripeKey);
      } catch (error) {
        console.error("Error fetching Stripe publishable key:", error);
      }
    };
    fetchStripeKey();
  }, [slug]);

  // setUserTour cuando cambie el tourUUID
  useEffect(() => {
    if (formData.tourUUID) setUserTour(formData.tourUUID);
  }, [formData.tourUUID, setUserTour]);

  // Calcular start/end en ms (y luego convertimos a s para el backend)
  useEffect(() => {
    if (!date || !time) return;
    const startMs = DateTime.fromISO(`${date}T${time}`, {
      zone: "Europe/Madrid",
    }).toMillis();
    const endMs = startMs + tourDuration * 60 * 1000;
    setFormData((prev) => ({
      ...prev,
      bookingStart: startMs,
      bookingEnd: endMs,
    }));
    console.log("bookingStart/End (ms):", {
      startMs,
      endMs,
      startSec: toSecs(startMs),
      endSec: toSecs(endMs),
    });
  }, [date, time, tourDuration, setFormData]);

  // hasMinors -> formData
  useEffect(() => {
    setFormData((prev) => ({ ...prev, hasMinors }));
  }, [hasMinors, setFormData]);

  // Personas totales
  useEffect(() => {
    if (locksPeopleSelection) return;
    const safeAdults = Math.max(1, Number(adults) || 1);
    const safeMinors = Math.max(0, Number(minorsTotal) || 0);
    const newPersons = safeAdults + safeMinors;
    setFormData((prev) => {
      const prevAdults = Number(prev.adults) || 0;
      const prevPersons = Number(prev.persons) || 0;
      if (prevAdults === safeAdults && prevPersons === newPersons) return prev;
      return { ...prev, adults: safeAdults, persons: newPersons };
    });
  }, [adults, locksPeopleSelection, minorsTotal, setFormData]);

  useEffect(() => {
    const rangesForPricing =
      durationForPricing?.people_prices ?? durationForPricing?.peoplePrices;
    const hasPeopleRanges =
      Array.isArray(rangesForPricing) && rangesForPricing.length > 0;
    if (!usesPeopleAndTimesPricing && !hasPeopleRanges) return;

    const nextUnitPrice = getPriceByPeopleRanges(
      rangesForPricing,
      formData.persons,
      formData.priceUnit,
      durationForPricing?.price
    );

    setFormData((prev) => {
      const current = Number(prev.priceUnit ?? 0) || 0;
      if (current === nextUnitPrice) return prev;
      return { ...prev, priceUnit: nextUnitPrice };
    });
  }, [
    usesPeopleAndTimesPricing,
    formData.persons,
    formData.priceUnit,
    setFormData,
    durationForPricing,
  ]);

  // Telefono
  const handlePhoneChange = (countryCode, phoneNumber) => {
    const combinedPhone = [countryCode, phoneNumber].filter(Boolean).join(" ");
    setFormData((prev) => ({ ...prev, phone: combinedPhone, countryCode }));
  };

  // Checkout
  const handleStripeCheckout = async () => {
    setIsProcessingPayment(true);
    try {
      const csrfToken = await getCsrfToken();

      const fullName = `${formData.firstName || ""} ${
        formData.lastName || ""
      }`.trim();

      const payload = {
        name: fullName,
        email: formData.email,
        phone: formData.phone,
        country: formData.country,
        sprice: formData.priceUnit,
        totalUnits: formData.totalUnits,
        tuktukModels: formData.tuktukModels,
        origin_url: window.location.href,
        bookingEnd: toSecs(formData.bookingEnd),
        bookingStart: toSecs(formData.bookingStart),
        tourUUID: formData.tourUUID,
        customPickup: formData.customPickup,
        customPickupRequested: formData.customPickupRequested,
        persons: formData.persons,
        adults: formData.adults,
        minorsTotal: formData.minorsTotal,
        minors: formData.minors,
        hasMinors: formData.hasMinors,
        minors2to5: formData.minors2to5,
        minors6to10: formData.minors6to10,
        minors11to17: formData.minors11to17,
      };

      const response = await axiosAuth.post("/checkout", payload, {
        headers: { "X-CSRF-TOKEN": csrfToken },
      });

      const session = response.data;
      if (session?.free_booking && session?.url) {
        window.location.assign(session.url);
        return;
      }

      if (!stripePromise) {
        setValidationMessages({ apiError: "Stripe no esta listo todavia." });
        return;
      }

      const stripe = await stripePromise;
      if (!session?.id) {
        throw new Error("Stripe session id missing");
      }

      const result = await stripe.redirectToCheckout({ sessionId: session.id });
      if (result.error) {
        console.error(result.error.message);
      }
    } catch (error) {
      console.error("Error creating checkout session:", error);
      if (error.response?.data?.errors) {
        setValidationMessages(error.response.data.errors);
      } else if (error.response?.data?.error) {
        setValidationMessages({ apiError: error.response.data.error });
      } else {
        setValidationMessages({ apiError: "An unexpected error occurred" });
      }
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleConfirmAndSetAppointment = () => handleStripeCheckout();

  // Menores
  const handleAddMinor = () => {
    setMinors((prev) => [...prev, { age: 2 }]);
    setMinorsTotal((prevTotal) => prevTotal + 1);
    setHasMinors(true);
    setFormData((prev) => ({
      ...prev,
      minorsTotal: (prev.minorsTotal || 0) + 1,
      minors2to5: (prev.minors2to5 || 0) + 1,
    }));
  };

  const updateMinorCategories = (minorsList) => {
    let minors2to5 = 0,
      minors6to10 = 0,
      minors11to17 = 0;
    minorsList.forEach((minor) => {
      const age = Number.parseInt(minor.age, 10);
      if (age >= 2 && age <= 5) minors2to5 += 1;
      else if (age >= 6 && age <= 10) minors6to10 += 1;
      else if (age >= 11 && age <= 17) minors11to17 += 1;
    });
    return { minors2to5, minors6to10, minors11to17 };
  };

  const handleRemoveMinor = (index) => {
    const updatedMinors = minors.filter((_, i) => i !== index);
    setMinors(updatedMinors);
    setMinorsTotal(updatedMinors.length);
    const categories = updateMinorCategories(updatedMinors);
    setFormData((prev) => ({
      ...prev,
      ...categories,
      minorsTotal: updatedMinors.length,
      hasMinors: updatedMinors.length > 0,
    }));
    setValidationMessages((prev) => {
      const updated = { ...prev };
      delete updated[`minor-${index}`];
      return updated;
    });
  };

  const handleMinorAgeChange = (index, value) => {
    const age = Number.parseInt(value, 10);
    if ((age >= 2 && age <= 17) || value === "") {
      const updatedMinors = [...minors];
      updatedMinors[index].age = value;
      setMinors(updatedMinors);
      const categories = updateMinorCategories(updatedMinors);
      setFormData((prev) => ({
        ...prev,
        minorsTotal: updatedMinors.length,
        minors2to5: categories.minors2to5,
        minors6to10: categories.minors6to10,
        minors11to17: categories.minors11to17,
      }));
      setValidationMessages((prev) => {
        const updated = { ...prev };
        delete updated[`minor-${index}`];
        return updated;
      });
    } else {
      setValidationMessages((prev) => ({
        ...prev,
        [`minor-${index}`]: "La edad debe estar entre 2 y 17 anos.",
      }));
    }
  };

  // Sync minors -> formData
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      minors: minors.map((m) => ({ age: m.age })),
      minorsTotal: minors.length,
    }));
  }, [minors, setFormData]);

  // Precio total
  useEffect(() => {
    setTotalPrice(
      (Number(pricingPreview.total ?? 0) || 0) +
        (Number(customPickupConfig.chargeAmount ?? 0) || 0)
    );
  }, [pricingPreview.total, customPickupConfig.chargeAmount]);

  // -------- Fallback para cargar modelos --------
  useEffect(() => {
    const numOrNull = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };

    const fetchWithClient = async (client, label, params) => {
      try {
        const res = await client.get("/tuktuksAPI/get-simple", {
          params,
          headers: { "Cache-Control": "no-cache" },
        });
        pushLog({ level: "log", message: `[${label}] status ${res.status}` });
        if (Array.isArray(res.data)) {
          return res.data;
        }
        // respuesta inesperada
        console.error(`[${label}] Respuesta inesperada:`, res.data);
        return null;
      } catch (err) {
        const status = err?.response?.status;
        const data = err?.response?.data;
        pushLog({
          level: "error",
          message: `[${label}] ${status || "ERR"} al cargar modelos`,
          stack: typeof data === "string" ? data : JSON.stringify(data || {}),
        });
        return { __error: true, status, data };
      }
    };

    const fetchTuktukModels = async () => {
      if (!formData.tourUUID || !formData.bookingStart || !formData.bookingEnd)
        return;

      // Normalizamos sprice
      const sprice =
        numOrNull(formData?.sprice) ?? numOrNull(formData?.priceUnit) ?? 0;

      const params = {
        tourUUID: formData.tourUUID,
        sprice,
        bookingStart: toSecs(formData.bookingStart),
        bookingEnd: toSecs(formData.bookingEnd),
      };

      pushLog({
        level: "log",
        message: `Intento carga modelos -> ${JSON.stringify(params)}`,
      });

      // Orden de intento: PUBLIC -> COOKIES -> AUTH
      const attempts = [
        { client: axiosPublic, label: "PUBLIC" },
        { client: axiosCookieOnly, label: "COOKIE" },
        { client: axiosAuth, label: "AUTH" },
      ];

      for (const { client, label } of attempts) {
        const out = await fetchWithClient(client, label, params);
        if (out && !out.__error) {
          // exito
          const models = out.map((tuktuk) => ({
            value: tuktuk.tuktukUUID,
            label: tuktuk.model,
            availableUnits: tuktuk.availableUnits,
            maxPeople: tuktuk.maxPeople,
          }));
          setTuktukModels([
            { value: "", label: texts("form.modelData.selectModel") },
            ...models,
          ]);
          // solo setear por primera vez si no existe
          setFormData((prev) => {
            if (!prev.tuktukModels || prev.tuktukModels.length === 0) {
              return { ...prev, tuktukModels: [{ value: "", units: 1 }] };
            }
            return prev;
          });
          return;
        }

        // Si fue un error 401 con PUBLIC, seguimos probando COOKIE/AUTH
        // Si fue un 500 con AUTH, seguimos no sea que PUBLIC/COOKIE funcione
      }

      // Si llegamos aqui, no hubo exito
      console.error("No se pudo cargar modelos con ninguno de los clientes.");
    };

    fetchTuktukModels();
    if (formData.tourUUID && !optionalFields) {
      fetchOptionalFields(formData.tourUUID);
    }
  }, [
    formData.tourUUID,
    formData.bookingStart,
    formData.bookingEnd,
    formData?.sprice,
    formData?.priceUnit,
    axiosPublic,
    axiosCookieOnly,
    axiosAuth,
    fetchOptionalFields,
    optionalFields,
    setFormData,
    texts,
  ]);
  // -------- Fin fallback --------

  // Errores de hijos
  const handleFieldErrors = (fieldErrors) => {
    setErrors((prev) => ({ ...prev, ...fieldErrors }));
  };

  const isCapacityInsufficient = useMemo(() => {
    if (!formData.persons || formData.persons <= 0) return false;

    const totalCapacity = (formData.tuktukModels || []).reduce((acc, m) => {
      const t = tuktukModels.find((tt) => tt.value === m.value);
      return acc + (t ? t.maxPeople * m.units : 0);
    }, 0);

    return totalCapacity < formData.persons;
  }, [formData.persons, formData.tuktukModels, tuktukModels]);

  // Validacion formulario
  const isFormIncomplete = () => {
    if (Object.values(errors).some((e) => e)) return true;

    if (
      !formData.firstName ||
      !formData.lastName ||
      !formData.email ||
      !formData.phone ||
      formData.phone.length < 6 ||
      !formData.country
    ) {
      return true;
    }

    if (!formData.persons || formData.persons <= 0) return true;

    if (
      customPickupConfig.enabled &&
      customPickupConfig.requested &&
      !String(formData.customPickup || "").trim()
    ) {
      return true;
    }

    if (formData.tuktukModels?.some((m) => !m.value || m.units <= 0))
      return true;

    if (!tuktukModels || tuktukModels.length === 0) return true;

    return isCapacityInsufficient;
  };

  const isPurchaseDisabled = isFormIncomplete();
  const displayPrice = isCapacityInsufficient ? "-" : totalPrice;
  const displayCurrency = isCapacityInsufficient ? "" : "\u20AC";
  const checkoutValidationMessages = getCheckoutValidationMessages(
    validationMessages,
    texts("paymentIntent.error")
  );

  return (
    <>
      <div
        className={`confirmationScreen ${
          isPhone ? "confirmationScreen--mobile-drawer pt-[10svh]" : ""
        }`}
      >
        <PButtonPure
          className="confirmationBackButton"
          icon="arrow-left"
          hideLabel={true}
          onClick={onBack}
          style={{ marginLeft: isPhone ? "" : spacingFluidXSmall }}
        />

        <div
          className="conditionalContainer"
          style={{ display: isPhone ? "flex" : "grid" }}
        >
          {/* Dynamic */}
          <div
            className="leftConfirmationScreen"
            style={{
              padding: isPhone
                ? spacingFluidSmall
                : `0 ${spacingFluidLarge} ${spacingFluidXLarge} ${spacingFluidXLarge}`,
              margin: isPhone
                ? `0 ${spacingFluidSmall} ${spacingFluidLarge} ${spacingFluidSmall}`
                : "",
              gap: isPhone ? spacingFluidLarge : spacingFluidMedium,
            }}
          >
            {!isPhone && <PDivider direction="vertical" className="vDivider" />}
            {!locksPeopleSelection && (
              <BlurFade className="z-0">
                <CText
                  size="x-large"
                  weight={isPhone ? "bold" : ""}
                  style={{ marginBottom: spacingFluidMedium }}
                >
                  {texts("form.headers.bookData")}
                </CText>

                <RenderHumanDataFields
                  adults={adults}
                  setAdults={setAdults}
                  handleMinorAgeChange={handleMinorAgeChange}
                  handleRemoveMinor={handleRemoveMinor}
                  minors={minors}
                  setMinors={setMinors}
                  textOrigin={textOrigin}
                  textBranch="form.humanData"
                  handleAddMinor={handleAddMinor}
                  headerText={texts("form.headers.passengers")}
                />
              </BlurFade>
            )}

            <BlurFade className="relative">
              <div
                className="flex flex-col"
                style={{
                  gap: isPhone ? spacingFluidLarge : spacingFluidMedium,
                }}
              >
                <div
                  style={locksPeopleSelection ? { display: "none" } : undefined}
                  aria-hidden={locksPeopleSelection}
                >
                  <AutoTuktukModelsSection
                    persons={formData.persons}
                    tuktukModels={tuktukModels}
                    formData={formData}
                    setFormData={setFormData}
                    textBranch="form.modelData"
                    textOrigin={textOrigin}
                    userData={userData}
                    isMinVehicleSmall={isMinVehicleSmall}
                  />
                </div>

                <PersonalInfoFields
                  formData={formData}
                  setFormData={setFormData}
                  phoneRef={phoneRef}
                  countryCodeRef={countryCodeRef}
                  onPhoneChange={handlePhoneChange}
                  textOrigin={textOrigin}
                  textBranch="form.personalData"
                  phoneTexts={"form.personalData.phone"}
                  textHeader={texts("form.headers.personal")}
                  isPhone={isPhone}
                  onError={handleFieldErrors}
                />
                {isPhone && (
                  <div
                    className="mobileStickyDrawerFieldSpacer"
                    aria-hidden="true"
                  />
                )}
              </div>
            </BlurFade>
          </div>

          {/* Static */}
          {!isPhone && (
            <BlurFade className="h-full">
              <div
                className="rightConfirmationScreen"
                style={{ padding: spacingFluidSmall }}
              >
                <RenderStaticData
                  titleStyle={textXLargeStyle}
                  time={time}
                  date={date}
                  totalPrice={displayPrice}
                  formData={formData}
                  isFormIncompleteProp={isPurchaseDisabled}
                  handleConfirmAndSetAppointment={
                    handleConfirmAndSetAppointment
                  }
                  texts={texts}
                  currency={displayCurrency}
                  pickupConfig={customPickupConfig}
                />
              </div>
            </BlurFade>
          )}
        </div>

        {checkoutValidationMessages.length > 0 && (
          <div className="error-messages">
            {checkoutValidationMessages.map(({ field, message }) => (
              <PInlineNotification key={field}>
                {message}
              </PInlineNotification>
            ))}
          </div>
        )}
      </div>

      {isPhone && (
        <StickyDrawer
          price={displayPrice}
          currency={displayCurrency}
          isProcessingPayment={isProcessingPayment}
          spinnerText={texts("paymentIntent.processing")}
          isOpen={isDrawerOpen}
          onChangeOpenState={setIsDrawerOpen}
          isPurchaseDisabled={isPurchaseDisabled}
        >
          <RenderStaticData
            titleStyle={textXLargeStyle}
            time={time}
            date={date}
            totalPrice={displayPrice}
            formData={formData}
            isFormIncompleteProp={isPurchaseDisabled}
            handleConfirmAndSetAppointment={handleConfirmAndSetAppointment}
            texts={texts}
            currency={displayCurrency}
            isPhone={isPhone}
            pickupConfig={customPickupConfig}
          />
        </StickyDrawer>
      )}

      {isProcessingPayment && (
        <div className="spinnerOverlay">
          <div
            className="spinnerContainer"
            style={{
              ...tileBasedStyle,
              ...dropShadowLowStyle,
              padding: spacingFluidMedium,
            }}
          >
            <PSpinner />
            <CText>{texts("paymentIntent.processing")}</CText>
          </div>
        </div>
      )}

      {/* ---------- MINI CONSOLA ---------- */}
      {/* <div
        className="miniConsole"
        style={{
          marginTop: spacingFluidLarge,
          borderTop: "1px solid rgba(0,0,0,0.1)",
          padding: spacingFluidSmall,
        }}
      >
        <div
          className="miniConsoleHeader"
          style={{ display: "flex", alignItems: "center", gap: "12px", justifyContent: "space-between" }}
        >
          <CText size="large" weight="bold">
            Consola (errores): {miniConsoleLogs.length}
          </CText>
          <div style={{ display: "flex", gap: "8px" }}>
            <PButtonPure
              icon={miniConsoleOpen ? "minus" : "plus"}
              hideLabel={true}
              onClick={() => setMiniConsoleOpen((o) => !o)}
            />
            <PButtonPure icon="delete" hideLabel={true} onClick={() => setMiniConsoleLogs([])} />
          </div>
        </div>

        {miniConsoleOpen && (
          <div
            className="miniConsoleBody"
            style={{
              marginTop: spacingFluidSmall,
              maxHeight: "30svh",
              overflow: "auto",
              fontFamily:
                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
              fontSize: "12px",
              background: "rgba(0,0,0,0.04)",
              borderRadius: "8px",
              padding: "12px",
            }}
          >
            {miniConsoleLogs.length === 0 ? (
              <div style={{ opacity: 0.7 }}>Sin errores capturados.</div>
            ) : (
              miniConsoleLogs.map((log, idx) => (
                <div key={`${log.ts}-${idx}`} style={{ borderBottom: "1px dashed rgba(0,0,0,0.1)", padding: "8px 0" }}>
                  <div style={{ opacity: 0.7 }}>
                    [{log.ts}] [{log.level?.toUpperCase() || "LOG"}]
                  </div>
                  <div style={{ whiteSpace: "pre-wrap" }}>{log.message}</div>
                  {log.where && <div style={{ opacity: 0.7 }}>@ {log.where}</div>}
                  {log.stack && (
                    <pre style={{ whiteSpace: "pre-wrap", margin: "6px 0 0", opacity: 0.85 }}>{log.stack}</pre>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div> */}
      {/* ---------- FIN MINI CONSOLA ---------- */}
    </>
  );
};

export default ConfirmationScreen;
